<template>
  <div class="mua-modal__wrapper" v-if="showModal && selectedItem">
    <div class="mua-modal__screen" @click="closeModal"></div>
    <div class="mua-modal resume-request-modal">

      <template v-if="mode === 'confirm'">
        <div class="resume-request-modal-header">
          <div class="mua-modal__header-content">
            <h3 class="resume-request-detail-title">Questionnaire not reviewed yet</h3>
            <p class="mua-modal__subtitle">
              Patient: {{ selectedItem.patientName }} · Completed: {{ selectedItem.completedAt || '—' }}
            </p>
          </div>
          <a href="#" class="mua-modal__close" @click.prevent="closeModal">
            <i class="fa fa-times"></i>
          </a>
        </div>

        <div class="resume-request-modal-body">
          <p class="mua-confirm-copy">
            The patient has answered the medical update questionnaire, but the answers have not been reviewed yet.
            The prescription cannot be resumed until you review them.
          </p>
          <p class="mua-confirm-copy">
            Review the answers now, or leave the prescription paused and come back to it later.
          </p>
        </div>

        <div class="rr-modal-actions">
          <button type="button" class="rr-modal-nav-btn rr-modal-nav-btn--secondary" @click="closeModal">Close</button>
          <button type="button" class="rr-modal-nav-btn" @click="switchToAnswers">View Answers</button>
        </div>
      </template>

      <template v-else>
        <div class="resume-request-modal-header">
          <div class="mua-modal__header-content">
            <h3 class="resume-request-detail-title">Questionnaire answers – {{ selectedItem.patientName }}</h3>
            <p class="mua-modal__subtitle">
              Completed: {{ selectedItem.completedAt }} · Prescriptions status: {{ selectedItem.scriptStatus }}
            </p>
          </div>
          <a href="#" class="mua-modal__close" @click.prevent="closeModal">
            <i class="fa fa-times"></i>
          </a>
        </div>

        <div class="resume-request-modal-body">
          <div
              v-for="(response, rIdx) in selectedItem.responses"
              :key="rIdx"
              class="rr-detail-section"
          >
            <h3>{{ response.title }} <span class="mua-score">Score: {{ response.score != null ? response.score : 'N/A' }}</span></h3>
            <div class="rr-detail-grid mua-questions-grid">
              <div
                  v-for="(q, qIdx) in response.questions"
                  :key="qIdx"
                  class="rr-detail-item mua-question-item"
              >
                <label>{{ qIdx + 1 }}. {{ q.label || ('Question ' + (qIdx + 1)) }}</label>
                <span>{{ getOptionLabel(response.answers, q.key) }}</span>
              </div>
            </div>
          </div>

          <p v-if="reviewError" class="mua-error">{{ reviewError }}</p>
        </div>

        <div class="rr-modal-actions">
          <button type="button" class="rr-modal-nav-btn rr-modal-nav-btn--secondary" @click="closeModal" :disabled="submitting">
            Close (leave paused)
          </button>
          <button type="button" class="rr-modal-nav-btn" @click="markReviewed" :disabled="submitting">
            {{ submitting === 'review' ? 'Marking…' : 'Mark reviewed' }}
          </button>
          <button type="button" class="rr-modal-nav-btn" @click="markReviewedAndUnpause" :disabled="submitting">
            {{ submitting === 'unpause' ? 'Unpausing…' : 'Mark reviewed and unpause' }}
          </button>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
const SCALE_LABELS = {
  0: 'Not at all',
  1: 'Several days',
  2: 'More than half the days',
  3: 'Nearly every day'
};

export default {
  name: 'MedicalUpdateAnswersModal',
  props: {
    items: {
      type: Array,
      default: function () {
        return [];
      }
    }
  },
  data() {
    return {
      showModal: false,
      selectedItem: null,
      mode: 'answers',
      submitting: false,
      reviewError: '',
      pushedItems: []
    };
  },
  methods: {
    availableItems() {
      const propItems = Array.isArray(this.items) ? this.items : [];
      return propItems.concat(this.pushedItems);
    },
    openModal(requestId) {
      const id = parseInt(requestId, 10);
      const item = this.availableItems().find(function (i) {
        return i.id === id;
      });
      if (item) {
        this.selectedItem = item;
        this.mode = 'answers';
        this.reviewError = '';
        this.showModal = true;
      }
    },
    openWithItem(item, options) {
      if (!item || typeof item !== 'object') {
        return;
      }
      const existing = this.pushedItems.findIndex(function (i) {
        return i.id === item.id;
      });
      if (existing === -1) {
        this.pushedItems.push(item);
      } else {
        this.pushedItems.splice(existing, 1, item);
      }
      this.selectedItem = item;
      this.mode = (options && options.confirmFirst) ? 'confirm' : 'answers';
      this.reviewError = '';
      this.showModal = true;
    },
    switchToAnswers() {
      this.mode = 'answers';
      this.reviewError = '';
    },
    postAction(path) {
      const tokenEl = document.querySelector('input[name="_token"]');
      const token   = tokenEl ? tokenEl.value : '';

      return fetch(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json, text/html;q=0.9,*/*;q=0.8',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': token,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: '_token=' + encodeURIComponent(token)
      }).then(function (response) {
        if (!response.ok) {
          throw new Error(path + ' failed with status ' + response.status);
        }
        return response;
      });
    },
    markReviewed() {
      if (!this.selectedItem || this.submitting) {
        return;
      }

      this.submitting  = 'review';
      this.reviewError = '';

      const self = this;
      const id   = this.selectedItem.id;

      this.postAction('/dashboard/medical-updates/' + id + '/review').then(function () {
        self.showModal    = false;
        self.selectedItem = null;
        self.submitting   = false;
        const toastEl = document.getElementById('SigUpdateMessage');
        if (toastEl && typeof window.showSigToast === 'function') {
          window.showSigToast(
            toastEl,
            'Marked reviewed. The prescription remains paused — unpause from the dashboard when you are ready.',
            { isError: false }
          );
        }
      }).catch(function (err) {
        self.submitting  = false;
        self.reviewError = err && err.message
          ? err.message
          : 'Could not mark the questionnaire as reviewed. Please try again.';
      });
    },
    markReviewedAndUnpause() {
      if (!this.selectedItem || this.submitting) {
        return;
      }

      this.submitting  = 'unpause';
      this.reviewError = '';

      const self = this;
      const id   = this.selectedItem.id;

      this.postAction('/dashboard/medical-updates/' + id + '/review')
        .then(function () {
          return self.postAction('/dashboard/medical-updates/' + id + '/unpause');
        })
        .then(function () {
          window.location.reload();
        })
        .catch(function (err) {
          self.submitting  = false;
          self.reviewError = err && err.message
            ? err.message
            : 'Could not unpause the prescription. Please try again or use the dashboard.';
        });
    },
    closeModal() {
      if (this.submitting) {
        return;
      }
      this.showModal = false;
      this.selectedItem = null;
      this.mode = 'answers';
      this.reviewError = '';
    },
    getOptionLabel(answers, questionKey) {
      if (!answers || typeof answers !== 'object') {
        return '—';
      }
      const val = answers[questionKey];
      if (val === undefined || val === null || val === '') {
        return '—';
      }
      const num = parseInt(val, 10);
      if (SCALE_LABELS.hasOwnProperty(num)) {
        return SCALE_LABELS[num];
      }
      return String(val);
    },
    bindViewAnswersButtons() {
      const self = this;
      const buttons = document.querySelectorAll('[data-medical-update-view-answers]');
      buttons.forEach(function (el) {
        el.addEventListener('click', self.onViewAnswersClick);
      });
      this._boundButtons = buttons;
    },
    onViewAnswersClick(e) {
      e.preventDefault();
      const requestId = e.currentTarget.getAttribute('data-request-id');
      if (requestId) {
        this.openModal(requestId);
      }
    },
    unbindViewAnswersButtons() {
      const self = this;
      if (this._boundButtons) {
        this._boundButtons.forEach(function (el) {
          el.removeEventListener('click', self.onViewAnswersClick);
        });
        this._boundButtons = null;
      }
    }
  },
  mounted() {
    this.bindViewAnswersButtons();
    window.medicalUpdateAnswersModal = this;
  },
  beforeDestroy() {
    this.unbindViewAnswersButtons();
    if (window.medicalUpdateAnswersModal === this) {
      window.medicalUpdateAnswersModal = null;
    }
  }
};
</script>

<style scoped>
/* Wrapper & overlay – same as resume request modal */
.mua-modal__wrapper {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  z-index: 10000;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.mua-modal__screen {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

/* Modal box – match .resume-request-modal */
.mua-modal.resume-request-modal {
  background: #fff;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 999999;
  padding: 0.25rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  position: absolute;
  border-radius: 15px;
  max-width: 700px;
  width: 90%;
  height: 70vh;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.resume-request-modal-header {
  padding: 35px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
}

.mua-modal__header-content {
  flex: 1;
}

.resume-request-detail-title {
  margin: 0 0 0.5rem 0;
  color: #243153;
  font-size: 18px;
}

.mua-modal__subtitle {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.mua-modal__close {
  position: absolute;
  right: 10px;
  top: 10px;
  font-size: 26px;
  font-weight: bold;
  color: #1f577a;
  text-decoration: none;
  line-height: 1;
  cursor: pointer;
}

.mua-modal__close:hover {
  color: #243153;
}

.resume-request-modal-body {
  overflow-y: auto;
  flex: 1;
  padding: 2rem;
  min-height: 0;
}

.rr-detail-section {
  margin-bottom: 30px;
}

.rr-detail-section:last-child {
  margin-bottom: 0;
}

.rr-detail-section h3 {
  margin-bottom: 15px;
  color: #243153;
  font-size: 18px;
  border-bottom: 2px solid #243153;
  padding-bottom: 8px;
}

.mua-score {
  font-weight: normal;
  color: #666;
  margin-left: 0.5rem;
}

/* Questions as detail grid – single column for Q&A list */
.mua-questions-grid.rr-detail-grid {
  grid-template-columns: 1fr;
  gap: 12px;
}

.mua-question-item.rr-detail-item {
  padding: 10px 12px;
  background: #f9f9f9;
  border-radius: 8px;
  border-left: 3px solid #243153;
}

.mua-question-item.rr-detail-item label {
  font-weight: bold;
  color: #666;
  font-size: 14px;
  display: block;
  margin-bottom: 4px;
}

.mua-question-item.rr-detail-item span {
  color: #333;
  font-size: 16px;
}

.rr-modal-actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 0;
  padding: 20px 35px;
  border-top: 1px solid #eee;
  flex-shrink: 0;
}

.rr-modal-nav-btn {
  background-color: #243153;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
}

.rr-modal-nav-btn:hover {
  background-color: #1a223a;
}

.rr-modal-nav-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rr-modal-nav-btn--secondary {
  background-color: #fff;
  color: #243153;
  border: 1px solid #243153;
}

.rr-modal-nav-btn--secondary:hover {
  background-color: #f2f4fa;
}

.mua-confirm-copy {
  color: #333;
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 1rem;
}

.mua-confirm-copy:last-child {
  margin-bottom: 0;
}

.mua-error {
  color: #b0413e;
  font-size: 14px;
  margin: 1rem 0 0;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/MedicalUpdateAnswersModal.vue